import type { LayoutKey, VirtuosaLayout } from './types';

export interface VoicingPlacement {
  left: LayoutKey[];
  right: LayoutKey[];
  /** True when at least one MIDI from the voicing has no cell in the layout. */
  partial: boolean;
}

function pickMostCompact(opts: LayoutKey[][]): LayoutKey[] {
  if (opts.some((o) => o.length === 0)) {
    return [];
  }

  let best: LayoutKey[] = [];
  let bestSpread = Infinity;

  const recurse = (depth: number, picked: LayoutKey[]): void => {
    if (depth === opts.length) {
      let spread = 0;
      for (let i = 0; i < picked.length; i++) {
        for (let j = i + 1; j < picked.length; j++) {
          const d = Math.hypot(
            picked[i].x - picked[j].x,
            picked[i].y - picked[j].y,
          );
          if (d > spread) {
            spread = d;
          }
        }
      }

      if (spread < bestSpread) {
        bestSpread = spread;
        best = picked.slice();
      }

      return;
    }

    for (const k of opts[depth]) {
      picked.push(k);
      recurse(depth + 1, picked);
      picked.pop();
    }
  };

  recurse(0, []);

  return best;
}

function rightmost(keys: LayoutKey[]): LayoutKey {
  let best = keys[0];
  for (const k of keys) {
    if (k.x > best.x) {
      best = k;
    }
  }

  return best;
}

// Place an explicit list of MIDIs on the keyboard. Auto-detects a bass
// note (lowest MIDI separated from the next by a full octave) and routes
// it to the left hand; the rest goes to the right hand, picked compactly.
//
// Used for hint inputs that already encode a specific voicing (e.g. the
// current inversion in chord-practice), distinct from `placeChord`,
// which always emits root position.
export function placeVoicing(
  midis: readonly number[],
  layout: VirtuosaLayout,
): VoicingPlacement | null {
  if (midis.length === 0) {
    return null;
  }

  const sorted = [...midis].sort((a, b) => a - b);

  const hasBass =
    sorted.length >= 2 && sorted[1] - sorted[0] >= 12;
  const bassMidi = hasBass ? sorted[0] : null;
  const triadMidis = hasBass ? sorted.slice(1) : sorted;

  let left: LayoutKey[] = [];
  let partial = false;

  if (bassMidi !== null) {
    const bassOpts = (layout.byMidi.get(bassMidi) ?? []).filter(
      (k) => k.hand === 'left',
    );

    if (bassOpts.length > 0) {
      left = [rightmost(bassOpts)];
    } else if (layout.byMidi.has(bassMidi)) {
      left = [rightmost(layout.byMidi.get(bassMidi) ?? [])];
    } else {
      partial = true;
    }
  }

  const rightCandidates = triadMidis.map((m) => {
    const all = layout.byMidi.get(m) ?? [];
    const onRight = all.filter((k) => k.hand === 'right');

    return onRight.length > 0 ? onRight : all;
  });

  for (const opts of rightCandidates) {
    if (opts.length === 0) {
      partial = true;
    }
  }

  const right = pickMostCompact(rightCandidates);

  return { left, right, partial };
}
